
//#region import libraries
import React, { useEffect, useState } from "react";
import { useHistory } from 'react-router-dom';
// bootstrap
import { Row, Button, Form, Col } from 'react-bootstrap';
//tremor
import { Flex, Subtitle, Title } from '@tremor/react'

//custom components
import { CustomSpinner } from '_atoms';
import { ActionsModal } from '_components/molecules';
import QuestionForm from "./questions-form";
import SurveyPreviewView from "./survey-preview-view";
import { typeNeedsOptions } from "./survey-question-types";
import { flattenSurveyForLanguage } from "./surveys-utils";

//select-react
import Select from "react-select"
//redux
import { useDispatch, useSelector } from "react-redux";
import { selectIata } from "_redux/slices/main-screen/user";
import { postSurveyQuestionnaire, selectPostQuestionnaireStatus, reset } from "_redux/slices/analytics/review-control-panel";
//styles
import { Colors, Spacing } from "_styles";

import { FaCirclePlus, FaEye } from 'react-icons/fa6';

//#endregion
const SurveyQuestionnaireForm = (props) => {
    const history = useHistory()
    //we can add more languages since it will be full dynamically
    const languages = [
        { label: 'English', value: "en" },
        { label: 'Arabic', value: "ar" },
        { label: 'Spanish', value: "es" },
    ]

    //#region redux
    const dispatch = useDispatch();
    const postQuestionnaireStatus = useSelector(selectPostQuestionnaireStatus);

    const iata = useSelector(selectIata);
    //#endregion

    //#region local state
    const [render, setRender] = useState(true);                       // flag used to stop use effects running on render

    const [surveyAudience, setSurveyAudience] = useState("passenger");
    const [surveyType, setSurveyType] = useState("arrivals");
    const [groupTitle, setGroupTitle] = useState("");
    const [questions, setQuestions] = useState([]);                   // Will be initialized when languages are selected
    const [errors, setErrors] = useState({});
    const [enableSaving, setEnableSaving] = useState(false);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [showSurveySettings, setShowSurveySettings] = useState(true);
    const [showPreview, setShowPreview] = useState(false);
    const [previewLang, setPreviewLang] = useState("en");
    //#endregion

    // Preview only complete questions (skip the trailing blank one being edited).
    const previewableQuestions = questions.filter((q) => q?.type);
    const previewQuestions = flattenSurveyForLanguage({ questions: previewableQuestions }, previewLang);

    const openPreview = () => {
        setPreviewLang(selectedLanguages[0]?.value || "en");
        setShowPreview(true);
    };

    // Initialize questions when languages are selected
    useEffect(() => {
        if (selectedLanguages.length > 0 && questions.length === 0) {
            const initialQuestion = {
                text: selectedLanguages.reduce((acc, lang) => {
                    acc[lang.value] = "";
                    return acc;
                }, {}),
                type: "",
                options: selectedLanguages.reduce((acc, lang) => {
                    acc[lang.value] = [];
                    return acc;
                }, {}),
            };
            setQuestions([initialQuestion]);
        }
    }, [selectedLanguages]);



    //#region functions

    const handleAdd = () => {
        setErrors({})

        const lastQuestion = questions[questions.length - 1];
        const isTextValid = selectedLanguages.every((lang) => lastQuestion.text[lang.value]?.trim());

        // Check if options are valid (for types that need options)
        const isOptionsValid = typeNeedsOptions(lastQuestion.type)
            ? selectedLanguages.every(
                (lang) => lastQuestion.options[lang.value]?.length > 0
            )
            : true;

        // For rating type, check starsCount instead of options
        const isRatingValid = lastQuestion.type === "rating"
            ? lastQuestion.starsCount > 0
            : true;

        // For matrix-scale5, check if items exist
        const isMatrixValid = lastQuestion.type === "matrix-scale5"
            ? lastQuestion.items?.length > 0
            : true;

        const isValid = lastQuestion.type && isTextValid && isOptionsValid && isRatingValid && isMatrixValid;
        console.log(isValid, "isValid...", { isTextValid, isOptionsValid, isRatingValid, isMatrixValid })

        if (!isValid) {
            setErrors((prev) => ({
                ...prev,
                preQuestionNotValid: "Complete the current question before adding a new one",
            }));
            return;
        }
        // Initialize new question structure with multilingual support
        const newQuestion = {
            text: selectedLanguages.reduce((acc, lang) => {
                acc[lang.value] = "";
                return acc;
            }, {}),
            type: "",
            options: selectedLanguages.reduce((acc, lang) => {
                acc[lang.value] = [];
                return acc;
            }, {}),
        };
        setQuestions([...questions, newQuestion]);
    };


    // remove question
    const handleDeleteQuestion = (questionIndex) => {
        let tempQuestions = Array.isFullArray(questions) ? [...questions] : [];
        tempQuestions.splice(questionIndex, 1); // Removes 1 item at the specified index
        setQuestions([...tempQuestions]);
    }

    const saveSurvey = () => {
        let questionsArray = questions;
        if (questionsArray[questions.length - 1].text === "" && questionsArray[questions.length - 1].type === "") {
            questionsArray.pop()
        }
        const data = {
            iata,
            surveyType,
            groupTitle,
            surveyAudience,
            active: false,
            questions: questionsArray,
            languages: selectedLanguages
        }
        dispatch(postSurveyQuestionnaire(data))
    }

    const handleCancel = () => {
        props?.handleCancel()
        history.push("/feedback")
    }

    // show Questions Form
    const handleShowQuestions = () => {
        setShowSurveySettings(false)
    }
    const handleShowSurveySettings = () => {
        setShowSurveySettings(true)
    }
    //#endregion

    //#region useEffect


    // error listener
    useEffect(() => {

        const validationErrors = {};

        if (!render && questions.length > 0) {
            const lastQuestion = questions[questions.length - 1];

            // Check if question text is valid for all selected languages
            const hasValidText = selectedLanguages.every(
                (lang) => lastQuestion.text?.[lang.value]?.trim()
            );

            // Check if question type is selected
            const hasType = lastQuestion.type?.length > 0;

            // For rating type, check starsCount
            const isRatingValid = lastQuestion.type === "rating"
                ? lastQuestion.starsCount > 0
                : true;

            // For matrix-scale5, check items
            const isMatrixValid = lastQuestion.type === "matrix-scale5"
                ? lastQuestion.items?.length > 0
                : true;

            // For types that need options (customOptions, checkbox and their hybrids)
            const isOptionsValid = typeNeedsOptions(lastQuestion.type)
                ? selectedLanguages.every(
                    (lang) => lastQuestion.options?.[lang.value]?.length > 0
                )
                : true;

            // Validate questions
            if (!hasValidText || !hasType || !isOptionsValid || !isRatingValid || !isMatrixValid) {
                validationErrors.questionsError = "Please add at least one valid question";
            }

            // Validate group title
            if (groupTitle.trim().length === 0) {
                validationErrors.surveyTitleError = "Please enter a valid survey title";
            }

            // Update errors state
            setErrors(validationErrors);

            // Enable saving only if there are no errors
            setEnableSaving(Object.keys(validationErrors).length === 0);

        }

    }, [questions, groupTitle, selectedLanguages])

    //
    useEffect(() => {
        if (postQuestionnaireStatus === "succeeded") {
            setQuestions([{ text: "", type: "", options: [] }]);
            setGroupTitle("")
            dispatch(reset())
            history.push("/feedback")
        }
    }, [postQuestionnaireStatus])

    // stop use effect from running on render
    useEffect(() => {
        setRender(false);
    }, []);



    //#endregion

    return (
        <div className="mt-3">
            {showSurveySettings ? <Row>

                {/* title */}
                <Row>
                    <Col xs={8}>
                        <Title>Create Survey</Title>
                        <Subtitle>Create a survey template by customizing the survey languages, title, audience, booking type and the series of questions asked</Subtitle>
                    </Col>
                </Row>


                {/* survey title */}
                <Row className="mt-4">
                    <Col xs={12} md={6} lg={6}>
                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Survey Title</Form.Label>
                        <Subtitle>This will help you identify this survey when navigating on the portal, passengers will not be shown this information</Subtitle>

                        <Col xs={5} className='mt-2'>
                            <Form.Control onChange={(e) => setGroupTitle(e.target.value)} value={groupTitle} type="text" placeholder="Survey Title" />
                            {errors && errors.surveyTitleError && <p style={{ color: Colors.TEXT_COLOR }} className="text-danger">{errors.surveyTitleError}</p>}
                        </Col>
                    </Col>

                    <Col xs={12} md={6} lg={6}>
                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Survey Languages</Form.Label>
                        <Subtitle>Choose the languages that the survey is available in, you can choose one or multiple languages</Subtitle>

                        <Col xs={5} className='mt-2'>
                            <Select
                                isMulti
                                options={languages}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                onChange={(selection) => {
                                    setSelectedLanguages(selection);
                                }}
                                value={selectedLanguages}
                            />
                        </Col>

                    </Col>

                </Row>


                <Row className="mt-4">
                    <Col xs={12}>

                        {/* audiences */}
                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Survey Audience</Form.Label>
                        <Subtitle>Choose who you want to survey, you can choose to survey either the passengers or the staff</Subtitle>

                        <div style={{ display: 'flex' }}>
                            <Form.Check type="radio" label="Passenger" value="passenger" name="surveyType" checked={surveyAudience === "passenger"} onChange={(e) => setSurveyAudience(e.target.value)} />

                            <div style={{ marginLeft: Spacing.SCALE_28 }}>
                                <Form.Check type="radio" label="Staff" value="staff" checked={surveyAudience === "staff"} onChange={(e) => setSurveyAudience(e.target.value)} />
                            </div>
                            <div style={{ marginLeft: Spacing.SCALE_28 }}>
                                <Form.Check type="radio" label="Email" value="email-survey" name="surveyType" checked={surveyAudience === "email-survey"} onChange={(e) => setSurveyAudience(e.target.value)} />
                            </div>
                        </div>

                    </Col>



                </Row>
            </Row>
                :
                <Row className="mt-4">

                    {/* survey questions*/}
                    <Col md={12}>
                        <Col style={{ justifyContent: "space-between", display: "flex", alignItems: "center", paddingInlineEnd: "32px", marginBottom: "17px" }}>
                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, margin: 0 }}>Questions</Form.Label>
                            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={openPreview}
                                    disabled={previewableQuestions.length === 0}
                                >
                                    <FaEye style={{ marginRight: 6 }} /> Preview
                                </Button>
                                <FaCirclePlus
                                    onClick={handleAdd}
                                    color={"lightgrey"}
                                    size={28}
                                    style={{ cursor: "pointer" }}
                                />
                            </div>
                        </Col>

                        <QuestionForm
                            questions={questions}
                            setQuestions={setQuestions}
                            selectedLanguages={selectedLanguages}
                            handleDeleteQuestion={handleDeleteQuestion}
                        />
                    </Col>

                </Row>
            }
            <Row className="mt-4">

                <Flex>

                    {!showSurveySettings ?
                        <Button
                            variant={"outline-danger"}
                            style={{ width: 200 }}
                            onClick={handleShowSurveySettings}>Back</Button>
                        : <Button
                            variant={"outline-danger"}
                            style={{ width: 200 }}
                            onClick={handleCancel}>Cancel</Button>}

                    {showSurveySettings ?
                        <Button
                            variant={"outline-primary"}
                            style={{ width: 200 }}
                            disabled={!Array.isFullArray(selectedLanguages) || !groupTitle}
                            onClick={handleShowQuestions}>Next</Button>
                        : <Button
                            variant={"outline-primary"}
                            style={{ width: 200 }}
                            disabled={!enableSaving}
                            onClick={saveSurvey}>{postQuestionnaireStatus === "loading" ? <CustomSpinner /> : "Save Survey"}</Button>
                    }

                </Flex>
            </Row>

            {/* Live preview of the unsaved draft */}
            <ActionsModal
                show={showPreview}
                handleClose={() => setShowPreview(false)}
                title={groupTitle ? `Preview: ${groupTitle}` : "Survey Preview"}
                subtitle={"This is how passengers will see the survey. Responses are not saved."}
                minWidth="620px"
                maxWidth="620px"
            >
                <SurveyPreviewView
                    questions={previewQuestions}
                    languages={selectedLanguages}
                    language={previewLang}
                    onLanguageChange={setPreviewLang}
                    groupTitle={groupTitle}
                />
            </ActionsModal>
        </div>
    )
}

export default SurveyQuestionnaireForm;